import React, { useContext } from 'react';
import {
  type ConnectionState,
  type EdgeProps,
  getBezierPath,
  getSmoothStepPath,
  useConnection,
} from '@xyflow/react';
import { EditorContext } from '../EditorContext';
import { edgeEmphasisFor } from '../connectionFocus';
import type { EdgeAccent, ProcessEdgeData } from '../graphTypes';
import {
  EdgePath,
  InteractionPath,
  WireEndpoint,
  WireGroup,
  type RunEdgeState,
} from './BranchedEdge.styled';
import { buildOrthoPath, isForwardOrthoLayout } from './buildOrthoPath';

// Fallback corner radius for `getSmoothStepPath` — used when the
// forward-ortho layout doesn't fit (backward wires, ports too close).
// React Flow's smoothstep internally clamps arcs to half the shorter
// leg, so this is an upper bound; the rendered arc usually lands
// smaller. Picked to match the node-card 36px corner radius so the
// fallback still reads in the same curvature family as the cards.
const ORTHO_FALLBACK_BORDER_RADIUS = 36;

const selectConnectionInProgress = (c: ConnectionState): boolean => c.inProgress;

/**
 * Custom edge type used everywhere on the process canvas. Stroke is
 * tinted by the pre-computed `data.accent` (default, approved, rejected),
 * and the path generator switches on the live `wireStyle` from the
 * editor context:
 *
 *   - bezier -> smooth bezier curves (the default React-Flow look)
 *   - ortho  -> rounded right-angles ("fillet" wires from the design)
 *
 * The accent is stamped onto the edge data at translate-time
 * (`dtoToReactFlow`) and at connect-time (`useProcessGraph.connect`) by
 * resolving the source port's label on a Gate node, so reading from
 * pre-computed data here keeps render cycles cheap and lets tests mount
 * the edge without a full React Flow store + node set.
 *
 * The wire-style read uses {@code useContext} directly (rather than the
 * {@code useEditorContext} helper which throws when the context is
 * missing) so unit tests that mount the edge without an editor provider
 * still render -- they fall back to the bezier default.
 */
const BranchedEdge: React.FC<EdgeProps> = ({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  data,
  markerEnd,
}) => {
  const accent: EdgeAccent =
    (data as ProcessEdgeData | undefined)?.accent ?? 'default';
  const editor = useContext(EditorContext);
  const useOrtho = editor?.wireStyle === 'ortho';
  const published = editor?.readOnly ?? false;
  const connecting = useConnection(selectConnectionInProgress);
  const focus =
    selected || connecting
      ? null
      : edgeEmphasisFor(editor?.connectionFocus, id);

  let runState: RunEdgeState = 'idle';
  if (editor?.runStateForNode) {
    const srcStatus = editor.runStateForNode(source).status;
    const tgtStatus = editor.runStateForNode(target).status;
    if (srcStatus === 'failed' || tgtStatus === 'failed') {
      runState = 'failed';
    } else if (srcStatus === 'completed' && tgtStatus === 'completed') {
      runState = 'done';
    } else if (srcStatus === 'completed' && tgtStatus === 'running') {
      runState = 'running';
    }
  }

  // Ortho path. For the standard L→R layout we use the design's bespoke
  // `buildOrtho` geometry (short source stub + big arcs + long target
  // approach) which React Flow's smoothstep can't produce. For backward
  // wires or ports that are too close horizontally, fall back to
  // smoothstep so the path doesn't U-turn through the source node.
  let path: string;
  if (useOrtho && isForwardOrthoLayout(sourceX, targetX)) {
    path = buildOrthoPath(sourceX, sourceY, targetX, targetY);
  } else if (useOrtho) {
    [path] = getSmoothStepPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
      borderRadius: ORTHO_FALLBACK_BORDER_RADIUS,
    });
  } else {
    [path] = getBezierPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
    });
  }

  return (
    <WireGroup
      data-testid={`process-edge-${id}`}
      data-accent={accent}
      data-wire-style={useOrtho ? 'ortho' : 'bezier'}
      data-run-state={runState}
      data-selected={selected ? 'true' : 'false'}
      data-focus={focus ?? 'idle'}
      $selected={Boolean(selected)}
      $runState={runState}
      $focus={focus}
    >
      <InteractionPath d={path} />
      <EdgePath
        id={id}
        d={path}
        markerEnd={markerEnd}
        $accent={accent}
        $selected={Boolean(selected)}
        $published={published}
        $runState={runState}
      />
      <WireEndpoint
        cx={sourceX}
        cy={sourceY}
        data-testid={`process-edge-${id}__endpoint-source`}
        $accent={accent}
        $selected={Boolean(selected)}
        $published={published}
        $runState={runState}
      />
      <WireEndpoint
        cx={targetX}
        cy={targetY}
        data-testid={`process-edge-${id}__endpoint-target`}
        $accent={accent}
        $selected={Boolean(selected)}
        $published={published}
        $runState={runState}
      />
    </WireGroup>
  );
};

export default BranchedEdge;
